import { autoserialize, autoserializeAs, inheritSerialization } from 'cerialize';
import moment from 'moment-timezone';
import { MiniRider } from './mini-rider';
import { PaymentCollectionType } from './payment-collection-type';
import { RideRequestLocation } from './ride-request-location';
import { RideRequestType } from './ride-request-type';
import { TransitProgramInfo } from './transit-program-info';
import { VehicleType } from './vehicle-type';
import { AbstractModel } from './abstract-model';
import { DateUtil, isNullOrUndefined } from '@uzurv/rideWss/app/util';

@inheritSerialization(AbstractModel)
export class RiderProtectedRideRequestDetail extends AbstractModel {
  @autoserialize
  itineraryId: number;

  @autoserializeAs(Date)
  itineraryPickupDate: Date;

  @autoserializeAs(RideRequestType)
  rideRequestType: RideRequestType;

  @autoserializeAs(VehicleType)
  vehicleType: VehicleType;

  @autoserialize
  activated: boolean;

  @autoserializeAs(Date)
  activatedAt: Date;

  @autoserializeAs(RideRequestLocation)
  pickupLocation: RideRequestLocation;

  @autoserializeAs(RideRequestLocation)
  dropoffLocation: RideRequestLocation;

  @autoserializeAs(Date)
  pickupDate: Date;

  @autoserializeAs(Date)
  dropoffDate: Date;

  @autoserialize
  dropoffDateProvided: boolean;

  @autoserialize
  polyline: string;

  @autoserialize
  distanceEstimate: number;

  @autoserialize
  durationEstimate: number;

  @autoserialize
  passengerCount: number;

  @autoserialize
  billablePassengerCount: number;

  @autoserialize
  accepted: boolean;

  @autoserializeAs(Date)
  acceptedDate: Date;

  @autoserializeAs(Date)
  cancelledDate: Date;

  @autoserialize
  active: boolean;

  @autoserialize
  requestFee: number;

  @autoserializeAs(PaymentCollectionType)
  riderPaymentCollectionType: PaymentCollectionType;

  @autoserialize
  paymentMethodId: number;

  @autoserialize
  incentive: number;

  @autoserialize
  tollsEstimate: number;

  @autoserialize
  timeZone: string;

  @autoserializeAs(MiniRider)
  miniRider: MiniRider;

  @autoserializeAs(TransitProgramInfo)
  transitProgramInfo: TransitProgramInfo;

  @autoserialize
  driverFareEstimate: number;

  @autoserialize
  riderNote: string;

  @autoserializeAs(Date)
  createdDate: Date;

  @autoserializeAs(Date)
  modifiedDate: Date;

  get driverFareEstimatePlusIncentive() {
    if (!isNullOrUndefined(this.incentive)) {
      return this.driverFareEstimate + this.incentive;
    }
    return this.driverFareEstimate;
  }

  get displayDate(): Date {
    if (!this.rideRequestType.onDemand) {
      return this.pickupDate;
    }
    return this.itineraryPickupDate;
  }

  /**
   * TODO: Refactor later using https://www.npmjs.com/package/localdate
   */
  get formattedDisplayDate(): string {
    if (!this.rideRequestType.onDemand) {
      return moment.tz(this.pickupDate, this.timeZone).format('M/DD/YY h:mm a (z)');
    }
    return moment(this.itineraryPickupDate).format('M/DD/YY');
  }

  static OnSerialized(instance: RiderProtectedRideRequestDetail, json: any): void {
    super.OnSerialized(instance, json);

    if (!isNullOrUndefined(instance.itineraryPickupDate)) {
      json.itineraryPickupDate = DateUtil.serializeLocalDate(instance.itineraryPickupDate);
    }

    if (!isNullOrUndefined(instance.activatedAt)) {
      json.activatedAt = DateUtil.serializeDate(instance.activatedAt);
    }

    if (!isNullOrUndefined(instance.pickupDate)) {
      json.pickupDate = DateUtil.serializeDate(instance.pickupDate);
    }

    if (!isNullOrUndefined(instance.dropoffDate)) {
      json.dropoffDate = DateUtil.serializeDate(instance.dropoffDate);
    }

    if (!isNullOrUndefined(instance.acceptedDate)) {
      json.acceptedDate = DateUtil.serializeDate(instance.acceptedDate);
    }

    if (!isNullOrUndefined(instance.cancelledDate)) {
      json.cancelledDate = DateUtil.serializeDate(instance.cancelledDate);
    }

    if (!isNullOrUndefined(instance.createdDate)) {
      json.createdDate = DateUtil.serializeDate(instance.createdDate);
    }

    if (!isNullOrUndefined(instance.modifiedDate)) {
      json.modifiedDate = DateUtil.serializeDate(instance.modifiedDate);
    }

    if (!isNullOrUndefined(instance.riderNote)) {
      const riderNote: string = instance.riderNote.trim();
      json.riderNote = riderNote.length === 0 ? null : riderNote;
    }
  }

  static OnDeserialized(instance: RiderProtectedRideRequestDetail, json: any): void {
    super.OnDeserialized(instance, json);

    if (!isNullOrUndefined(json['itineraryPickupDate'])) {
      instance.itineraryPickupDate = DateUtil.deserializeLocalDate(json['itineraryPickupDate']);
    }

    if (!isNullOrUndefined(json['activatedAt'])) {
      instance.activatedAt = DateUtil.deserializeDate(json['activatedAt']);
    }

    if (!isNullOrUndefined(json['pickupDate'])) {
      instance.pickupDate = DateUtil.deserializeDate(json['pickupDate']);
    }

    if (!isNullOrUndefined(json['dropoffDate'])) {
      instance.dropoffDate = DateUtil.deserializeDate(json['dropoffDate']);
    }

    if (!isNullOrUndefined(json['acceptedDate'])) {
      instance.acceptedDate = DateUtil.deserializeDate(json['acceptedDate']);
    }

    if (!isNullOrUndefined(json['cancelledDate'])) {
      instance.cancelledDate = DateUtil.deserializeDate(json['cancelledDate']);
    }

    if (!isNullOrUndefined(json['createdDate'])) {
      instance.createdDate = DateUtil.deserializeDate(json['createdDate']);
    }

    if (!isNullOrUndefined(json['modifiedDate'])) {
      instance.modifiedDate = DateUtil.deserializeDate(json['modifiedDate']);
    }
  }
}
